import React, { useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Textarea } from 'app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Settings, MapPin } from 'lucide-react';
import FormField from './components/FormField';
import SwitchField from './components/SwitchField';
import useOptimizedFormWatch from './hooks/useOptimizedFormWatch';

export default function ServicesFields() {
  const form = useFormContext();
  const watchedValues = useOptimizedFormWatch();

  // Memoize handlers to prevent unnecessary re-renders
  const handleConsultationChange = useCallback(
    (checked: boolean) => {
      form.setValue('requires_consultation', checked);
    },
    [form],
  );

  const handleCustomizableChange = useCallback(
    (checked: boolean) => {
      form.setValue('is_customizable', checked);
    },
    [form],
  );

  const handleServiceAvailableChange = useCallback(
    (checked: boolean) => {
      form.setValue('is_service_available', checked);
    },
    [form],
  );

  // Memoize conditional sections to prevent unnecessary re-renders
  const serviceOptionsSection = useMemo(() => {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <Settings className="w-4 h-4 mr-2" />
          Service Options
        </h3>
        <div className="space-y-3">
          <SwitchField
            label="Requires Consultation"
            description="Customer must schedule a consultation before booking"
            checked={watchedValues.requiresConsultation}
            onChange={handleConsultationChange}
          />
          <SwitchField
            label="Is Customizable"
            description="Service can be customized based on customer needs"
            checked={watchedValues.isCustomizable}
            onChange={handleCustomizableChange}
          />
          <SwitchField
            label="Service Available"
            description="Service is currently available for booking"
            checked={watchedValues.isServiceAvailable !== false}
            onChange={handleServiceAvailableChange}
          />
        </div>
      </div>
    );
  }, [
    watchedValues.requiresConsultation,
    watchedValues.isCustomizable,
    watchedValues.isServiceAvailable,
    handleConsultationChange,
    handleCustomizableChange,
    handleServiceAvailableChange,
  ]);

  const quantityConstraintsSection = useMemo(() => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Minimum Quantity" color="orange">
          <Input
            type="number"
            placeholder="1"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('min_quantity', { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Maximum Quantity" color="orange">
          <Input
            type="number"
            placeholder="10"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('max_quantity', { valueAsNumber: true })}
          />
        </FormField>
      </div>
    );
  }, [form]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-orange-50 to-red-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex shrink-0 items-center justify-center">
            <Settings className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Service Configuration
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Configure service-specific settings
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Service Duration */}
        <FormField
          label="Service Duration"
          helpText="How long does this service take to complete?"
          color="orange"
        >
          <Input
            placeholder="e.g., 2 hours, 1 day, 1 week"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('duration')}
          />
        </FormField>

        {/* Service Type */}
        <FormField label="Service Type" color="orange">
          <Select
            value={form.watch('service_type') || ''}
            onValueChange={value => form.setValue('service_type', value)}
          >
            <SelectTrigger className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm">
              <SelectValue placeholder="Select service type" />
            </SelectTrigger>
            <SelectContent position="popper" side="bottom" align="start">
              <SelectItem value="consultation">Consultation</SelectItem>
              <SelectItem value="installation">Installation</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="repair">Repair</SelectItem>
              <SelectItem value="training">Training</SelectItem>
              <SelectItem value="support">Support</SelectItem>
              <SelectItem value="custom">Custom Service</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {/* Service Location Type */}
        <FormField
          label="Service Location"
          helpText="Where can customers access this service?"
          color="orange"
        >
          <Select
            value={form.watch('service_location_type') || 'online'}
            onValueChange={value =>
              form.setValue('service_location_type', value)
            }
          >
            <SelectTrigger className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                <SelectValue placeholder="Select location type" />
              </div>
            </SelectTrigger>
            <SelectContent position="popper" side="bottom" align="start">
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="offline">Offline (In-person)</SelectItem>
              <SelectItem value="hybrid">
                Hybrid (Online + In-person)
              </SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {/* Quantity Constraints */}
        {quantityConstraintsSection}

        {/* Service Options */}
        {serviceOptionsSection}

        {/* Service Instructions */}
        <FormField
          label="Service Instructions"
          helpText="Instructions for service providers or customers"
          color="orange"
        >
          <Textarea
            placeholder="Provide detailed instructions for service delivery..."
            rows={4}
            className="bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('delivery_instructions')}
          />
        </FormField>
      </div>
    </div>
  );
}
